import { useQuery } from "@tanstack/react-query";
import { api, PaginatedAdvisorsResponse } from "@/services/api";

/**
 * Custom hook to fetch advisor matches for a PAB session
 * Uses React Query for proper caching, error handling, and retry logic
 * 
 * @param sessionId - The PAB session ID
 * @param enabled - Whether to enable the query (default: true)
 */
export const useAdvisorMatching = (sessionId: number | null, enabled: boolean = true) => {
  return useQuery<PaginatedAdvisorsResponse>({
    queryKey: ["advisorMatching", sessionId],
    queryFn: async () => {
      if (!sessionId) throw new Error("Session ID is required");
      return await api.pabSessions.getMatchingAdvisors(sessionId);
    },
    enabled: enabled && Boolean(sessionId), // Only run if enabled and sessionId exists
    staleTime: 0, // Always treat as stale so pabApprovalStatus is current
    retry: (failureCount, error: any) => {
      // Don't retry on rate limit (429) or auth errors
      if (error?.response?.status === 429) return false;
      if (error?.response?.status === 401) return false;
      if (error?.response?.status === 403) return false;
      // Don't retry on 404 (not found)
      if (error?.response?.status === 404) return false;
      // Retry other errors once
      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Always refetch on mount to get latest invitation statuses
  });
};
